import {useEffect} from 'react';
import {fetchAndSyncUserPreferences, updateUserPreferences} from '@/reducers/user-preferences.reducer';
import {
  updateFeatureToShowOnLoad,
  updateIsPosterEditorMobileVariant,
  updateIsPosterShared,
  updateIsPosterSharedInTeam,
  updateRecordOptionsOpenState,
} from '@Components/poster-editor/poster-editor-reducer';
import {handleFeatureToShowForMobile, initComWithIframe, isEditorMobileVariant, setMultiPageFlagInRedux} from '@Components/poster-editor/library/poster-editor-library';
import {openRecordAudioModal} from '@Modals/record-audio-modal';
import {watchIsAudioPlayable} from '@Utils/audio.util';
import {bindPosterEditorShortcuts, unbindPosterEditorShortcuts} from '@PosterWhiteboard/poster/poster-editor-shortcuts';
import type {PosterEditorConfig} from '@Components/poster-editor/poster-editor.types';
import {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';
import {useAppDispatch} from '@/hooks';
import {updateCurrentUserToRedux} from '@Utils/user.util';
import type {ElementData} from '@Libraries/add-media-library';
import {onAddItems} from '@Components/poster-editor/library/poster-editor-open-modals';
import {handlePasteOnPosterEditorCanvas} from '@Components/poster-editor/library/poster-editor-copy-paste-library';
import {getRecentStockItems} from '@Libraries/pmw-stock-media-library';
import {setRecentStockAvailability} from '@Panels/stock-panel/stock-panel-reducer';
import {MIN_STOCK_ITEMS_TO_DISPLAY} from '@Panels/stock-panel/stock-panel.types';
import {isOpenCustomImageAndVideoRecorder} from '@Libraries/record-image-and-video.library';
import {openRecordImageAndVideoModal} from '@Modals/record-image-and-video-modal';
import {openPosterAddItemsModal} from '@Modals/poster-add-items-modal';
import {AddItemOptions} from '@Panels/poster-add-items-panel/poster-add-items-panel';
import {RecorderMediaType} from '@Panels/record-image-and-video-panel/record-image-and-video.types';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import Emitter from '@/services/event-emitter';

interface PosterEditorInitProps {
  posterEditorConfig: PosterEditorConfig;
}

export const useInitPosterEditor = (data: PosterEditorInitProps): void => {
  const dispatch = useAppDispatch();
  const onAddRecordedItems = (item: ElementData): void => {
    onAddItems([item]);
  };

  const onUserLoggedIn = (): void => {
    void fetchAndSyncUserPreferences();
  };

  const initializeRecentStockItemFlag = async (): Promise<void> => {
    try {
      const response = await getRecentStockItems();
      dispatch(setRecentStockAvailability(response && response.length >= MIN_STOCK_ITEMS_TO_DISPLAY));
    } catch {
      dispatch(setRecentStockAvailability(false));
    }
  };

  const onCloseAudioRecordModal = (openRecordOptions = true): void => {
    dispatch(closeModal(MODALS_IDS.RECORD_AUDIO_MODAL));
    dispatch(updateRecordOptionsOpenState(openRecordOptions));
  };
  const onCloseVideoRecorderModal = (openRecordOptions = true): void => {
    dispatch(closeModal(MODALS_IDS.RECORD_IMAGE_AND_VIDEO_MODAL));
    dispatch(updateRecordOptionsOpenState(openRecordOptions));
  };

  useEffect(() => {
    if (window.posterEditor) {
      window.posterEditor.config = data.posterEditorConfig;
    }
    dispatch(updateUserPreferences(data.posterEditorConfig.userPreferences));
    dispatch(updateFeatureToShowOnLoad(data.posterEditorConfig.featureToShowOnLoad));
    dispatch(updateIsPosterShared(data.posterEditorConfig.isPosterShared ?? false));
    dispatch(updateIsPosterSharedInTeam(data.posterEditorConfig.isPosterSharedInTeam ?? false));
    handleFeatureToShowForMobile(data.posterEditorConfig.featureToShowOnLoad);
    if (data.posterEditorConfig.featureToShowOnLoad === FeatureToShowOnLoad.AUDIO_RECORDING) {
      openRecordAudioModal({onAddSelected: onAddRecordedItems, onClose: onCloseAudioRecordModal});
    }
    if (data.posterEditorConfig.featureToShowOnLoad === FeatureToShowOnLoad.VIDEO_RECORDING) {
      if (isOpenCustomImageAndVideoRecorder()) {
        openRecordImageAndVideoModal({recorderOpenedWithMediaType: RecorderMediaType.VIDEO, onAddSelected: onAddRecordedItems, onClose: onCloseVideoRecorderModal});
      } else {
        openPosterAddItemsModal({panelToShowOnOpen: AddItemOptions.RECORD});
      }
    }
    updateCurrentUserToRedux();
    watchIsAudioPlayable();
    initComWithIframe();
    window.addEventListener('paste', handlePasteOnPosterEditorCanvas);
    bindPosterEditorShortcuts();
    void initializeRecentStockItemFlag();
    setMultiPageFlagInRedux();
    dispatch(updateIsPosterEditorMobileVariant(isEditorMobileVariant()));
    Emitter.on('user:loggedIn', onUserLoggedIn);

    return (): void => {
      Emitter.off('user:loggedIn', onUserLoggedIn);
      window.removeEventListener('paste', handlePasteOnPosterEditorCanvas);
      unbindPosterEditorShortcuts();
    };
  }, []);
};
